import React, { useCallback, useMemo, useRef } from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import { Lottie } from '@remotion/lottie';
import { LottieElement } from '@/App/remotion/MainPlayer/types/Element/LottieElement';
import { useLottieLoaderAnimations } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useLottieLoaderAnimations';
import {
  useElementPositions,
  useLottieMediaBounds
} from '@/App/remotion/components/ElementComponent/hooks/useMediaBounds';
import { getAnimationStylesForFrame } from '@/App/remotion/MainPlayer/utils/animation/getAnimationStylesForFrame';
import { SelectionOutline } from '@/App/remotion/components/Outlines/SelectionOutline';
import { SelectionElementType } from '@/App/remotion/components/Outlines/types';
import { CompositionConfig, ElementPositions } from '@/App/remotion/MainPlayer/types/CompositionConfig';
import { constrainElementPosition } from '@/App/remotion/components/ElementComponent/ImageElementComponent/utils';

interface LottieElementComponentProps {
  element: LottieElement;
  onOutlineRelease?: CompositionConfig['onOutlineRelease'];
  canEditLottie?: boolean;
}

const LottieElementComponent: React.FC<LottieElementComponentProps> = ({
  element,
  onOutlineRelease,
  canEditLottie = false
}) => {
  const { getAnimationData } = useLottieLoaderAnimations();
  const lottieElementRef = useRef<HTMLDivElement>(null);

  const currentFrame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const { containerStyle = {}, style = {}, effects } = element;

  const { style: animationStyle } = getAnimationStylesForFrame(currentFrame, effects || [], fps);

  const getLottieBounds = useLottieMediaBounds(lottieElementRef, getAnimationData, element);

  const isLottieStyleContain = useMemo(
    () => style?.preserveAspectRatio !== 'xMidYMid slice',
    [style?.preserveAspectRatio]
  );

  const { positions, updatePositions, setPositions } = useElementPositions(
    containerStyle,
    isLottieStyleContain,
    getLottieBounds
  );

  const transformedContainerStyle = useMemo(
    () => ({
      ...containerStyle,
      ...animationStyle,
      ...(canEditLottie ? positions : {})
    }),
    [containerStyle, animationStyle, positions, canEditLottie]
  );

  const onSelectionOutlineRelease = useCallback(
    (newPositions: ElementPositions) => {
      const parentElement = lottieElementRef.current?.parentElement;
      const parentDimensions = parentElement ? parentElement.getBoundingClientRect() : null;

      const constrainedPositions = constrainElementPosition(newPositions, parentDimensions);

      setPositions(constrainedPositions);
      onOutlineRelease?.(constrainedPositions, element);
    },
    [onOutlineRelease, element, setPositions]
  );

  const renderLottie = useCallback(() => {
    const animationData = getAnimationData(element);
    if (!animationData) {
      return null;
    }

    return (
      <Lottie animationData={animationData} style={style} preserveAspectRatio={style.preserveAspectRatio as any} />
    );
  }, [element, getAnimationData, style]);

  return (
    <>
      <AbsoluteFill
        ref={lottieElementRef}
        style={{
          ...transformedContainerStyle,
          containerType: 'inline-size',
          overflow: 'hidden'
        }}
        data-testid="lottie-element-preview"
      >
        {renderLottie()}
      </AbsoluteFill>

      {canEditLottie && (
        <SelectionOutline
          positions={positions}
          updatePositions={updatePositions}
          onOutlineRelease={onSelectionOutlineRelease}
          allowResize={true}
          elementType={SelectionElementType.LOTTIE}
          data={{ elementId: element.id, type: SelectionElementType.LOTTIE }}
          maintainAspectRatio={isLottieStyleContain}
        />
      )}
    </>
  );
};

export default LottieElementComponent;
